import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { PATHS } from '@/lib/routes'
import { Typography }   from '@/components/ui/Typography'
import { useCanTransfer } from '@/lib/useCanTransfer'
import { useMobileHeaderStore } from '@/store/mobile-header-store'
import { useBottomNav } from './useBottomNav'
import type { BottomNavItem } from './BottomNav.types'

// ---------------------------------------------------------------------------
// Icon factories — keyed by route
// ---------------------------------------------------------------------------

const NAV_ICONS: Record<string, (active: boolean) => ReactNode> = {
  [PATHS.dashboard]: (active) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
      <path d="M3 10.182V20a1 1 0 001 1h5v-5h6v5h5a1 1 0 001-1V10.182L12 3 3 10.182Z" fill={active ? 'color-mix(in srgb, currentColor 15%, transparent)' : 'none'} />
      <path d="M9 21v-5h6v5" opacity={active ? 0.5 : 1} />
    </svg>
  ),
  // Mismo ícono que IconMovements en FloatingTabBar.tsx (mobile).
  [PATHS.transactions]: (active) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={active ? 2 : 1.7} strokeLinecap="round" className="w-5 h-5">
      <path d="M8 7h12" />
      <path d="M8 12h12" />
      <path d="M8 17h8" />
      <circle cx="4" cy="7" r="1" fill="currentColor" stroke="none" />
      <circle cx="4" cy="12" r="1" fill="currentColor" stroke="none" />
      <circle cx="4" cy="17" r="1" fill="currentColor" stroke="none" />
    </svg>
  ),
  // send-money es la CTA primaria. Renderizamos un pill brand-gradient con
  // icono + label en lugar del FAB circular, manteniendo el mismo lenguaje
  // visual que el boton "Enviar" del dashboard y del sidebar.
  [PATHS.sendMoney]: (_active) => null,
  // Mismo ícono que IconBalances en FloatingTabBar.tsx (mobile).
  [PATHS.balances]: (active) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={active ? 2 : 1.7} className="w-5 h-5">
      <rect x="2" y="5" width="20" height="14" rx="2" />
      <path d="M2 10h20" strokeLinecap="round" />
      <path d="M6 15h4" strokeWidth={1.5} strokeLinecap="round" />
    </svg>
  ),
  [PATHS.moreOptions]: (active) => (
    <svg viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth={active ? 2.5 : 2} className="w-5 h-5">
      <circle cx="5"  cy="12" r="1.6" />
      <circle cx="12" cy="12" r="1.6" />
      <circle cx="19" cy="12" r="1.6" />
    </svg>
  ),
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function BottomNav() {
  const { navItems, isActive, qrEnabled, qrLabel, showLoginGlow, isRootNav } = useBottomNav()
  const { canTransfer, notifyNoPermission } = useCanTransfer()
  const hideBottomNav = useMobileHeaderStore(s => s.hideBottomNav)

  if (hideBottomNav || !isRootNav) return null

  const items: BottomNavItem[] = navItems.map(item => ({
    ...item,
    icon: NAV_ICONS[item.href] ?? (() => null),
  }))

  return (
    <nav className="lg:hidden fixed bottom-0 left-0 right-0 h-16 bg-[var(--color-surface)] border-t border-[var(--color-border)] flex items-center px-2 gap-1 z-20">
      {items.map(({ href, label, icon }) => {
        const active = isActive(href)
        // send-money es un FAB circular brand-gradient — solo icono, sin
        // label. Mantiene la jerarquia visual de CTA primaria en mobile
        // sin ocupar el espacio horizontal del label "Transferir".
        if (href === PATHS.sendMoney) {
          // Si el balance activo tiene QR habilitado, el FAB central pasa a ser
          // QR (ícono distinto + abre el flujo en modo QR vía ?mode=qr), igual
          // que el botón central del mobile. Si no, queda el de enviar.
          const fabIcon = qrEnabled ? (
            <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
              <rect x="3" y="3" width="7" height="7" rx="1" />
              <rect x="14" y="3" width="7" height="7" rx="1" />
              <rect x="3" y="14" width="7" height="7" rx="1" />
              <line x1="14" y1="14" x2="14" y2="14.01" />
              <line x1="21" y1="14" x2="21" y2="14.01" />
              <line x1="14" y1="21" x2="14" y2="21.01" />
              <line x1="21" y1="21" x2="21" y2="21.01" />
              <line x1="17.5" y1="17.5" x2="17.5" y2="17.51" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth={2.5} className="w-5 h-5 translate-y-px">
              <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          )

          // Transferir bloqueado: FAB inactivo (opacidad + not-allowed) + toast.
          if (!canTransfer) {
            return (
              <div key={href} className="relative -mt-4">
                {showLoginGlow && (<>
                  <span aria-hidden="true" className="absolute -inset-3 rounded-full bg-gradient-primary opacity-60 animate-ping pointer-events-none" style={{ animationDuration: '1s' }} />
                  <span aria-hidden="true" className="absolute -inset-1 rounded-full bg-gradient-primary opacity-80 animate-ping pointer-events-none" style={{ animationDuration: '1s', animationDelay: '0.4s' }} />
                </>)}
                <button
                  type="button"
                  onClick={notifyNoPermission}
                  aria-disabled="true"
                  aria-label={label}
                  className="flex items-center justify-center w-12 h-12 rounded-full bg-gradient-primary text-white shadow-brand opacity-50 cursor-not-allowed"
                >
                  {fabIcon}
                </button>
              </div>
            )
          }

          return (
            <div key={href} className="relative -mt-4">
              {showLoginGlow && (
                <span aria-hidden="true" className="absolute inset-0 rounded-full bg-gradient-primary opacity-70 animate-ping pointer-events-none" />
              )}
              <Link
                to={qrEnabled ? `${href}?mode=qr&camera=1` : href}
                aria-label={qrEnabled ? qrLabel : label}
                className="flex items-center justify-center w-12 h-12 rounded-full bg-gradient-primary text-white shadow-brand hover:opacity-90 active:scale-[0.95] transition-all"
              >
                {fabIcon}
              </Link>
            </div>
          )
        }
        return (
          <Link
            key={href}
            to={href}
            className={[
              'flex-1 flex flex-col items-center justify-center gap-0.5 h-full transition-colors duration-150',
              active ? 'text-[var(--color-accent)]' : 'text-[var(--color-text-muted)]',
            ].join(' ')}
          >
            {icon(active)}
            <Typography variant="caption-semibold" color="inherit">{label}</Typography>
          </Link>
        )
      })}
    </nav>
  )
}
